/**
 * @date: 2022-04-15
 * @author: Kevin Hsiau <khsiau@max.md>
 * @copy: (C) Copyright 2022 MaxMD Corporation, All Rights Reserved.
 */


import React, {useEffect, useRef, useState} from "react";
import {Backdrop, Box, Button, CircularProgress, styled} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import LinearWithValueLabel from "../utils/LinearProgressWithLabel";
import {terminologyByBatchType} from "../utils/terminology";
import {printTime} from "../utils/cert-helper";
import {useDispatch, useSelector} from "react-redux";
import {cancelBatch, update} from "../features/allBatchesSlice";
import CircularStatic from "../Npi/NpiCircularProgressWithLabel";
import {ApiUrl} from "../utils/portal-urls";
import {handleHttpResponse} from "../utils/http-helpers";
import ShowSnackbar from "../utils/ShowSnackbar";
import {splitPath} from "./ExternalSourceTable";

const StyledDiv = styled('div')({
    paddingTop: 5,
    display: 'block',
    position: 'relative',
    width: '100%', margin: 'auto'
})

const StyledButton = styled(Button)({
    float: 'right', marginRight: 6, marginTop: 10,
    '& .MuiSvgIcon-root': {marginRight: 3}
})


const BatchStatus = {
    Init: 'Init',
    Running: 'Running',
    Completed: 'Completed',
    Cancelled: 'Cancelled',
    Failed: 'Failed',
    Interrupted: 'Interrupted'
}

export const assignSource = (type) => {
    switch (type) {
        case 'ImportNPIIncremental':
        case 'ImportNPIFullDump':
        case 'CheckNPI':
        case 'DeactivateNPIs':
            return 'nppes';
        case 'ImportSpecialtyValueSets':
        case 'CleanAndImportTaxonomyCodes':
            return 'nucc';
        case 'ImportValueSet':
            return 'servDescription';
        case 'ImportProvSuffixVS':
            return 'suffix';
        case 'ImportOrgTypeVS':
            return 'orgType';
        default: {
            // Amendment #1 A5: the dispatch type is the value set's own name, which is what the
            // allBatches reducers key terminologyBatches on.
            const terminology = terminologyByBatchType(type);
            return terminology ? terminology.key : '';
        }
    }

}

export default function ExternalSourceBatchProgressBar(props) {

    const {id, progress, status, createdTs, dataPath, type} = props.batch
    const account = useSelector(state => state.userAccount.userAccount);
    const [snackbarMsg, setSnackbarMsg] = useState({open: false, msg:'', msgLv: ''});
    const [loading, setLoading] = useState(false)

    const statusRef = useRef();
    const dispatch = useDispatch();

    useEffect(() => {
        const timer = setInterval(() => {
            if (statusRef.current !== BatchStatus.Completed && statusRef.current !== BatchStatus.Cancelled && statusRef.current !== BatchStatus.Failed) {
                getActiveBatchProgressById(id);
            }
        }, 750);

        return () => {
            clearInterval(timer);
        }
    }, []);

    useEffect(() => {
        statusRef.current = status;
    }, [progress])

    const getActiveBatchProgressById = async (id) => {
        try {
            const res = await fetch(ApiUrl.Batch.getBatchById.getPath(id));
            const data = await handleHttpResponse(res);
            const batchType =  assignSource(data.type);
            if (!!batchType) {
                dispatch(update({'type': batchType, 'id': id, 'batch': data}));
            }
        } catch (err) {
            console.error("Failed to fetch all active batch, " + err);
        }
    }


    const handleCancel = async () => {
        if (confirm("Are you sure you want to cancel the background process?")) {
            try {
                setLoading(true);
                const res = await fetch(ApiUrl.Batch.cancelBatch.getPath(id, account), {method: 'POST', body: ''});
                const data = await handleHttpResponse(res);
                const batchType = assignSource(data.type)
                if (!!batchType) {
                    dispatch(cancelBatch({ type: batchType, 'id': data.id}));
                }
                setSnackbarMsg({open: true, msg: 'Successfully cancel the process', msgLv: 'success'});
                console.log("Cancel Process Response ", data);
            } catch (err) {
                console.error(err);
                setSnackbarMsg({open: true, msg: 'Failed to cancel the process', msgLv: 'error'});
            } finally {
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    }

    return (
        <StyledDiv>
            <div>
                <CircularStatic progress={progress}/>
                <StyledButton size='small' onClick={handleCancel} startIcon={<CloseIcon />}>Cancel The Progress </StyledButton>
                <Box sx={{width: '100%'}}>
                    <LinearWithValueLabel progress={progress}/>
                </Box>
                <span>Currently processing NPI File Task: </span><br/>
                {assignSource(type) !== 'nucc'
                    ? <strong>{dataPath}</strong>
                    : <>
                        <strong>Individual URL:</strong>
                        {splitPath(dataPath)[0]}
                        <br/>
                        <strong>Non-Individual URL:</strong>
                        {splitPath(dataPath)[1]}
                      </>
                }
                <br/>
                <span>{`Started since ${printTime(createdTs)}`}</span>
            </div>

            <ShowSnackbar open={snackbarMsg.open} close={(c) => setSnackbarMsg(c)} msg={snackbarMsg} />
            <Backdrop sx={{zIndex: theme => theme.zIndex.drawer + 1, color: "#fff"}} open={loading}>
                <CircularProgress color={"inherit"}/>
            </Backdrop>
        </StyledDiv>
    )

}